import { Input, Select, Tabs, Button, Upload } from "antd";
import {
  DeleteFilled,
  EditOutlined,
  PlusCircleFilled,
  PlusOutlined,
  ArrowRightOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import Typography from "app/shared/Typography";
import PageHeader from "app/shared/PageHeader";
import "./HotelMailFollowupConfig.scss";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

const { TextArea } = Input;
const { TabPane } = Tabs;

interface DatasetInfo {
  datasourceType?: string;
  [key: string]: any;
}

interface Flow {
  id: number;
  type?: string;
  interval?: string;
  name?: string;
}

interface EmailFlow {
  id: number;
  type: string;
  interval: string;
  name: string;
  time: string;
  templateId: string;
  intervalAgainst: string;
}

interface ColumnMapping {
  id: number;
  sourceField: string; // fixed (uneditable if predefined)
  targetField: string;
  isPredefined?: boolean;
  isRequired?: boolean;
}

const HotelEmailConfigSetup: React.FC = () => {
  const location = useLocation();
  const [datasetInfo, setDatasetInfo] = useState<DatasetInfo>({});
  const [activeTab, setActiveTab] = useState("information");
  const navigate = useNavigate();
  const [editId, setEditId] = useState<string | null>(null);
  // Placeholder mapping for different source fields
  const sourceFieldPlaceholders: Record<string, string> = {
    email_bookingId: "invoice_number",
    email_sharableBookingId: "booking_id",
    email_checkindate: "checkin_date",
    email_bookingdate: "booking_date",
    email_checkoutdate: "checkout_date",
    company_name: "company_name",
    gstin: "gstin",
    company_address: "company_address",
    totalGST: "totalGST",
  };

  const [mappings, setMappings] = useState<ColumnMapping[]>([
    {
      id: 1,
      sourceField: "email_bookingId",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 2,
      sourceField: "email_sharableBookingId",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 3,
      sourceField: "email_checkindate",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 4,
      sourceField: "email_bookingdate",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 5,
      sourceField: "email_checkoutdate",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 6,
      sourceField: "company_name",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 7,
      sourceField: "gstin",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 8,
      sourceField: "company_address",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 9,
      sourceField: "totalGST",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
  ]);

  const [basicInfo, setBasicInfo] = useState({
    name: "",
    client_id: "",
    fromEmail: "",
    fromName: "",
    datasourceType: "MANUAL", // default
    databaseType: "Mongo", // default
    database: "",
    collection: "",
    mailType: "SINGLE", // default
    bookingid: "",
    subject: "",
    safety_valve: {
      key: "",
      value: "",
    },
    gstconfig: {
      email_hotel_gstin: "",
      gstkey: "",
    },
    searchPattern: "",
    gptContext: "",
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFileInfo, setUploadedFileInfo] = useState<any>(null);

  const requiredMark = (
    <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>
  );
  const [flows, setFlows] = useState<EmailFlow[]>([
    {
      id: 1,
      type: "EMAIL",
      interval: "",
      name: "",
      time: "",
      templateId: "",
      intervalAgainst: "",
    },
  ]);

  const requiredBasicFields = [
    "configName",
    "clientId",
    "fromEmail",
    "fromName",
    "databaseName",
    "tableName",
    "subject",
  ];
  const [action, setAction] = useState("create");

  useEffect(() => {
    if (
      location.state &&
      location.state.action === "edit" &&
      location.state.data
    ) {
      const receivedData = location.state.data;
      setAction("edit");

      setEditId(receivedData._id || null);

      // Prefill basic info
      setBasicInfo({
        name: receivedData.name || "",
        client_id: receivedData.client_id || "",
        fromEmail: receivedData.fromEmail || "",
        fromName: receivedData.fromName || "",
        datasourceType:
          receivedData.booking_data_source === "EMAIL" ? "EMAIL" : "MANUAL",
        databaseType: "Mongo",
        database: receivedData.database || "",
        collection: receivedData.collection || "",
        mailType: receivedData.mailType || "SINGLE",
        bookingid: receivedData.bookingid || "",
        subject: receivedData.subject || "",
        searchPattern: receivedData.searchPattern || "",
        gptContext: receivedData.gptContext || "",
        safety_valve: receivedData.safety_valve || { key: "", value: "" },
        gstconfig: receivedData.gstconfig || {
          email_hotel_gstin: "",
          gstkey: "",
        },
      });

      // Prefill flows
      if (
        receivedData.followup_flow &&
        Array.isArray(receivedData.followup_flow)
      ) {
        const prefilledFlows = receivedData.followup_flow.map(
          (flow: any, index: number) => ({
            id: index + 1,
            type: flow.type || "EMAIL",
            interval: flow.interval || "",
            name: flow.name || "",
            time: flow.time || "",
            templateId: flow.templateId || "",
          }),
        );
        setFlows(prefilledFlows);
      }

      // Prefill mappings
      if (receivedData.mapping && typeof receivedData.mapping === "object") {
        const prefilledMappings: ColumnMapping[] = [];

        // Loop over existing predefined mappings
        const predefinedMappings = [
          { sourceField: "email_bookingId", isRequired: true },
          { sourceField: "email_sharableBookingId", isRequired: true },
          { sourceField: "email_checkindate", isRequired: true },
          { sourceField: "email_bookingdate", isRequired: true },
          { sourceField: "email_checkoutdate", isRequired: true },
          { sourceField: "company_name", isRequired: true },
          { sourceField: "gstin", isRequired: true },
          { sourceField: "company_address", isRequired: true },
        ];

        predefinedMappings.forEach((mapping, index) => {
          prefilledMappings.push({
            id: index + 1,
            sourceField: mapping.sourceField,
            targetField: receivedData.mapping[mapping.sourceField] || "",
            isPredefined: true,
            isRequired: mapping.isRequired,
          });
        });

        // Handle any extra mappings if present (dynamic ones)
        const predefinedSourceFields = predefinedMappings.map(
          (m) => m.sourceField,
        );
        Object.entries(receivedData.mapping).forEach(
          ([sourceField, targetField]) => {
            if (!predefinedSourceFields.includes(sourceField)) {
              prefilledMappings.push({
                id: prefilledMappings.length + 1,
                sourceField: sourceField,
                targetField: typeof targetField === "string" ? targetField : "", // fallback
                isPredefined: false,
                isRequired: false,
              });
            }
          },
        );

        setMappings(prefilledMappings);
      }

      console.log("Received data for editing:", receivedData);
    }
  }, [location]);

  const handleChange = (sectionSetter: any, key: string, value: any) => {
    sectionSetter((prev: any) => ({ ...prev, [key]: value }));
  };

  const addFlow = () => {
    const newId =
      flows.length > 0 ? Math.max(...flows.map((f) => f.id)) + 1 : 1;
    setFlows([
      ...flows,
      {
        id: newId,
        type: "EMAIL",
        interval: "",
        name: "",
        time: "",
        templateId: "",
        intervalAgainst: "",
      },
    ]);
  };

  const removeFlow = (id: number) => {
    setFlows(flows.filter((flow) => flow.id !== id));
  };

  const handleFlowChange = (
    id: number,
    field: keyof EmailFlow,
    value: string,
  ) => {
    const updated = flows.map((flow) =>
      flow.id === id ? { ...flow, [field]: value } : flow,
    );
    setFlows(updated);
  };

  // const addNewFlow = () => {
  //   setFollowupFlow([
  //     ...followupFlow,
  //     {
  //       type: 'EMAIL',
  //       interval: '',
  //       name: '',
  //       time: '',
  //       templateId: '',
  //     },
  //   ]);
  // };

  // Function to call a dummy API with the configuration data
  const saveConfiguration = async () => {
    try {
      // Format the mapping data into an object with source fields as keys and target fields as values
      const formattedMappings: Record<string, string | string[]> =
        mappings.reduce((acc: Record<string, string | string[]>, mapping) => {
          if (mapping.sourceField && mapping.targetField) {
            acc[mapping.sourceField] = mapping.targetField;
          }
          return acc;
        }, {});

      // Format the flows to match the expected structure
      const formattedFlows = flows.map((flow) => ({
        type: flow.type,
        interval: flow.interval || null,
        name: flow.name || null,
        time: flow.time || null,
        templateId: flow.templateId || null,
      }));

      // Construct the API payload
      const payload: any = {
        ...(editId ? { _id: editId } : {}),
        // Basic information
        configName: basicInfo.name,
        clientId: basicInfo.client_id,
        fromEmail: basicInfo.fromEmail,
        fromName: basicInfo.fromName,
        datasourceType: basicInfo.datasourceType,
        databaseType: basicInfo.databaseType,
        databaseName: basicInfo.database,
        tableName: basicInfo.collection || null,
        mailType: basicInfo.mailType,
        bookingid: basicInfo.bookingid,
        subject: basicInfo.subject,
        searchPattern: basicInfo.searchPattern,
        gptContext: basicInfo.gptContext,

        // Safety valve and GST config
        safety_valve: {
          key: basicInfo.safety_valve?.key || "",
          value: basicInfo.safety_valve?.value || "",
        },
        gstconfig: {
          email_hotel_gstin: basicInfo.gstconfig?.email_hotel_gstin || "",
          gstkey: basicInfo.gstconfig?.gstkey || "",
        },

        // Mappings and flows
        mapping: formattedMappings,
        followup_flow: formattedFlows,
      };

      // Add file information if uploaded
      if (uploadedFile && uploadedFileInfo) {
        payload.uploadedFile = {
          name: uploadedFile.name,
          size: uploadedFile.size,
          type: uploadedFile.type,
          ...uploadedFileInfo, // Include any additional info from the upload API response
        };
      }

      console.log("Sending configuration to API:", payload);

      // Call the dummy API
      const response = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/addNewConfig",
        {
          //const response = await fetch("http://192.168.0.63:7000/api/hotelFollowUp/addNewConfig", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer your-auth-token", // Replace with actual auth token
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log("API response:", data);

      // message.success('Configuration saved successfully!');

      return data;
    } catch (error) {
      console.error("Error saving configuration:", error);
      // You can add error notification here
      // message.error('Failed to save configuration');
      throw error;
    }
  };

  const handleSave = () => {
    // Basic Info validations
    const {
      name,
      client_id,
      fromEmail,
      fromName,
      datasourceType,
      databaseType,
      database,
      collection,
      mailType,
      bookingid,
      subject,
    } = basicInfo;

    // Validate required fields - if file is uploaded, database and collection are not required
    if (
      !name ||
      !client_id ||
      !fromEmail ||
      !fromName ||
      !datasourceType ||
      !databaseType ||
      !mailType ||
      !bookingid ||
      !subject
    ) {
      message.error("Please fill all required basic information fields.");
      return;
    }

    // Either database + collection OR uploaded file is required
    if (!uploadedFile && (!database || !collection)) {
      message.error(
        "Please provide either Database Name and Collection/Table Name, or upload a file.",
      );
      return;
    }

    // Email flow validations (first flow only)
    const firstFlow = flows[0];
    if (
      !firstFlow.interval ||
      !firstFlow.templateId ||
      !firstFlow.time ||
      !firstFlow.intervalAgainst
    ) {
      message.error(
        "Please complete all required fields in the first email flow (Template ID, Interval, Time, and Interval Against).",
      );
      return;
    }

    // Predefined mappings validations
    const missingPredefined = mappings.some(
      (m) => m.isPredefined && !m.targetField,
    );

    if (missingPredefined) {
      message.error("Please complete all predefined column mappings.");
      return;
    }

    const loadingMessage = message.loading("Saving configuration...", 0);

    saveConfiguration()
      .then((response) => {
        loadingMessage();
        message.success("Configuration saved successfully!");
        setTimeout(() => navigate(-1), 1000);
      })
      .catch((error) => {
        loadingMessage();
        message.error("Failed to save configuration: " + error.message);
      });
  };

  const addMapping = () => {
    const newId =
      mappings.length > 0 ? Math.max(...mappings.map((m) => m.id)) + 1 : 1;
    setMappings([
      ...mappings,
      { id: newId, sourceField: "Email Booking ID", targetField: "" },
    ]);
  };

  const removeMapping = (idToRemove: number) => {
    setMappings((prev) => prev.filter((mapping) => mapping.id !== idToRemove));
  };

  const handleMappingChange = (
    id: number,
    field: keyof ColumnMapping,
    value: string,
  ) => {
    const updated = mappings.map((m) =>
      m.id === id ? { ...m, [field]: value } : m,
    );
    setMappings(updated);
  };

  const addMappingPair = () => {
    const newId = Date.now(); // safe enough
    setMappings((prev) => [
      ...prev,
      {
        id: newId,
        sourceField: "",
        targetField: "",
        isPredefined: false,
        isRequired: false,
      },
    ]);
  };

  const handleFileUpload = async (file: File) => {
    // Validate file type before upload (matching backend validation)
    const validMimeTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
      "text/csv", // .csv
    ];

    if (!validMimeTypes.includes(file.type)) {
      message.error(
        "Invalid file format. Only Excel (.xlsx/.xls) or CSV is allowed.",
      );
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("filename", file.name);
      formData.append("fileType", file.type);
      formData.append("clientId", basicInfo.client_id || "");

      const response = await fetch(
        "https:/stage-apiplatform.finkraftai.com/api/hotelFollowUp/uploadExcel",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            workspaceId: localStorage.getItem("currentWorkspace") || "",
          },
          body: formData,
        },
      );

      const data = await response.json();

      // Handle backend error responses (status 400, 500, etc.)
      if (!response.ok) {
        const errorMessage =
          data.message || `File upload failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      // Backend returns: { message, insertedCount }
      console.log("File upload response:", data);

      setUploadedFile(file);
      setUploadedFileInfo({
        message: data.message,
        insertedCount: data.insertedCount,
        filename: file.name,
      });

      // Show success message with inserted count
      const successMessage = data.insertedCount
        ? `${data.message || "File uploaded successfully!"} ${data.insertedCount} records inserted.`
        : data.message || "File uploaded successfully!";
      message.success(successMessage);

      // Set database and collection to email_followup_automation when file is uploaded
      setBasicInfo((prev) => ({
        ...prev,
        database: "email_followup_automation",
        collection: prev.name
          ? `${prev.name.toLowerCase().replace(/\s+/g, "_")}_data`
          : "uploaded_data",
      }));
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      message.error(`Failed to upload file: ${errorMessage}`);
      setUploadedFile(null);
      setUploadedFileInfo(null);
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <div className="HotelMailFollowupConfig">
      <PageHeader title="New Configuration Setup" />
      {/* {action === "edit" ? "Edit" : "Create"} */}
      <div className="ContentContainer">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="config-tabs"
        >
          <TabPane tab="Information & Flow" key="information">
            <div className="FormContainer">
              <div className="section-header">
                <Typography
                  variant="h5"
                  weight={500}
                  style={{ marginBottom: 16 }}
                >
                  Basic Information
                </Typography>
              </div>

              <div className="FormRow">
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Configuration Name {requiredMark}
                  </Typography>
                  <Input
                    placeholder="ie: Yatra follow-up EY"
                    style={{ height: 38 }}
                    // size="small"
                    value={basicInfo.name}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Client ID {requiredMark}
                  </Typography>
                  <Input
                    placeholder="ie: YATRA_FOLLOWUP_EY"
                    style={{ height: 38 }}
                    value={basicInfo.client_id}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        client_id: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="FormRow">
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    From Email {requiredMark}
                  </Typography>
                  <Input
                    placeholder="ie: gstinfo@finkraft.ai"
                    style={{ height: 38 }}
                    value={basicInfo.fromEmail}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        fromEmail: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    From Name {requiredMark}
                  </Typography>
                  <Input
                    placeholder="ie: GST Info"
                    style={{ height: 38 }}
                    value={basicInfo.fromName}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        fromName: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="FormRow">
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Data Source Type {requiredMark}
                  </Typography>
                  <Select
                    value={basicInfo.datasourceType}
                    style={{ width: "100%", height: "38px" }}
                    onChange={(selectedValue) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        datasourceType: selectedValue,
                      }))
                    }
                    options={[
                      { value: "MANUAL", label: "Manual" },
                      { value: "EMAIL", label: "Email Reading" },
                    ]}
                  />
                </div>
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Database Type {requiredMark}
                  </Typography>
                  <Select
                    value={basicInfo.databaseType}
                    style={{ width: "100%", height: "38px" }}
                    onChange={(value) =>
                      setBasicInfo((prev) => ({ ...prev, databaseType: value }))
                    }
                    options={[
                      { value: "MONGO", label: "Mongo DB" },
                      { value: "POSTGRE", label: "Postgre DB" },
                    ]}
                  />
                </div>
              </div>

              {basicInfo.datasourceType === "EMAIL" && (
                <div className="FormRow">
                  <div className="FormGroup">
                    <Typography variant="xs" style={{ marginBottom: 3 }}>
                      Search Pattern {requiredMark}
                    </Typography>
                    <Input
                      placeholder="ie: Renew*"
                      style={{ height: 38 }}
                      value={basicInfo.searchPattern}
                      onChange={(e) =>
                        setBasicInfo((prev) => ({
                          ...prev,
                          searchPattern: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="FormGroup" style={{ width: "100%" }}>
                    <Typography variant="xs" style={{ marginBottom: 3 }}>
                      GPT Context {requiredMark}
                    </Typography>
                    <TextArea
                      placeholder="Context for parsing email content"
                      autoSize={{ minRows: 3, maxRows: 5 }}
                      value={basicInfo.gptContext}
                      onChange={(e) =>
                        setBasicInfo((prev) => ({
                          ...prev,
                          gptContext: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              <div className="FormRow">
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Database Name {!uploadedFile && requiredMark}
                  </Typography>
                  <Input
                    placeholder="ie: hotel_vendor_master"
                    style={{ height: 38 }}
                    value={basicInfo.database}
                    onChange={(e) => {
                      const newDatabaseValue = e.target.value;
                      // Clear uploaded file if user starts typing database name
                      if (newDatabaseValue && uploadedFile) {
                        setUploadedFile(null);
                        setUploadedFileInfo(null);
                      }
                      setBasicInfo((prev) => ({
                        ...prev,
                        database: newDatabaseValue,
                      }));
                    }}
                    disabled={!!uploadedFile}
                  />
                </div>

                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Collection/Table Name {!uploadedFile && requiredMark}
                  </Typography>
                  <Input
                    placeholder="ie: yatra_hotel_booking"
                    style={{ height: 38 }}
                    value={basicInfo.collection}
                    onChange={(e) => {
                      const newCollectionValue = e.target.value;
                      // Clear uploaded file if user starts typing collection name
                      if (newCollectionValue && uploadedFile) {
                        setUploadedFile(null);
                        setUploadedFileInfo(null);
                      }
                      setBasicInfo((prev) => ({
                        ...prev,
                        collection: newCollectionValue,
                      }));
                    }}
                    disabled={!!uploadedFile}
                  />
                </div>
              </div>

              <div className="FormRow">
                <div className="FormGroup" style={{ width: "100%" }}>
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Upload File{" "}
                    {!basicInfo.database &&
                      !basicInfo.collection &&
                      requiredMark}
                  </Typography>
                  {uploadedFile ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Typography
                        variant="xs"
                        style={{ flex: 1, color: "#595959" }}
                      >
                        {uploadedFile.name}
                      </Typography>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setUploadedFile(null);
                          setUploadedFileInfo(null);
                          // Clear database and collection when file is removed
                          setBasicInfo((prev) => ({
                            ...prev,
                            database: "",
                            collection: "",
                          }));
                        }}
                        style={{ padding: 0 }}
                        title="Remove file"
                      />
                    </div>
                  ) : (
                    <Upload
                      beforeUpload={(file) => {
                        handleFileUpload(file);
                        return false; // Prevent auto upload
                      }}
                      showUploadList={false}
                      accept=".csv,.xlsx,.xls"
                      disabled={!!(basicInfo.database && basicInfo.collection)}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        style={{ width: "100%" }}
                        loading={uploadingFile}
                        disabled={
                          uploadingFile ||
                          !!(basicInfo.database && basicInfo.collection)
                        }
                      >
                        {uploadingFile ? "Uploading..." : "Upload File"}
                      </Button>
                    </Upload>
                  )}
                </div>
              </div>

              <div className="FormRow">
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Mail Type {requiredMark}
                  </Typography>
                  <Select
                    value={basicInfo.mailType}
                    style={{ width: "100%", height: "38px" }}
                    onChange={(value) =>
                      setBasicInfo((prev) => ({ ...prev, mailType: value }))
                    }
                    options={[
                      { value: "SINGLE", label: "Single" },
                      { value: "BULK", label: "Bulk" },
                    ]}
                  />
                </div>

                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Booking ID {requiredMark}
                  </Typography>
                  <Input
                    value={basicInfo.bookingid}
                    placeholder="ie: 123456"
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        bookingid: e.target.value,
                      }))
                    }
                    style={{ height: 38 }}
                  />
                </div>
              </div>

              {basicInfo.mailType === "BULK" && (
                <div className="FormRow">
                  <div className="FormGroup" style={{ width: "100%" }}>
                    <Typography variant="xs" style={{ marginBottom: 3 }}>
                      Group By
                    </Typography>
                    <Select
                      disabled
                      value="Hotel Name"
                      style={{ width: "100%", height: "38px" }}
                      options={[{ value: "Hotel Name", label: "Hotel Name" }]}
                    />
                  </div>

                  <div className="FormGroup" style={{ width: "100%" }}>
                    <Typography variant="xs" style={{ marginBottom: 3 }}>
                      Sort By
                    </Typography>
                    <Select
                      disabled
                      value="Total GST"
                      style={{ width: "100%", height: "38px" }}
                      options={[{ value: "Total GST", label: "Total GST" }]}
                    />
                  </div>
                </div>
              )}

              <div className="FormRow">
                <div className="FormGroup" style={{ width: "100%" }}>
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Subject {requiredMark}
                  </Typography>
                  <Input
                    value={basicInfo.subject}
                    placeholder="ie: Booking Confirmation"
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        subject: e.target.value,
                      }))
                    }
                    style={{ height: 38 }}
                  />
                </div>
              </div>

              <div className="FormRow">
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Safety Valve Key
                  </Typography>
                  <Input
                    value={basicInfo.safety_valve?.key}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        safety_valve: {
                          ...prev.safety_valve,
                          key: e.target.value,
                        },
                      }))
                    }
                    style={{ height: 38 }}
                  />
                </div>

                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Safety Valve Value
                  </Typography>
                  <Input
                    value={basicInfo.safety_valve?.value}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        safety_valve: {
                          ...prev.safety_valve,
                          value: e.target.value,
                        },
                      }))
                    }
                    style={{ height: 38 }}
                  />
                </div>
              </div>

              <div className="FormRow">
                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    GST Config - Hotel GSTIN
                  </Typography>
                  <Input
                    value={basicInfo.gstconfig?.email_hotel_gstin}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        gstconfig: {
                          ...prev.gstconfig,
                          email_hotel_gstin: e.target.value,
                        },
                      }))
                    }
                    style={{ height: 38 }}
                  />
                </div>

                <div className="FormGroup">
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    GST Config - GST Key
                  </Typography>
                  <Input
                    value={basicInfo.gstconfig?.gstkey}
                    onChange={(e) =>
                      setBasicInfo((prev) => ({
                        ...prev,
                        gstconfig: {
                          ...prev.gstconfig,
                          gstkey: e.target.value,
                        },
                      }))
                    }
                    style={{ height: 38 }}
                  />
                </div>
              </div>

              {datasetInfo?.datasourceType && (
                <>
                  <div className="section-header">
                    <Typography
                      variant="h5"
                      weight={500}
                      style={{ marginBottom: 16, marginTop: 24 }}
                    >
                      <EditOutlined style={{ marginRight: 8 }} />
                      Data Scrapper Setup
                    </Typography>
                  </div>

                  {datasetInfo?.datasourceType === "MANUAL" ? (
                    <div className="FormRow">
                      <div className="FormGroup">
                        <Typography variant="xs" style={{ marginBottom: 3 }}>
                          API Endpoint
                        </Typography>
                        <Input
                          placeholder="ie: https://travel-data-api.bcdtravel.com/v1/trips"
                          style={{ height: 38 }}
                        />
                      </div>

                      <div className="FormGroup">
                        <Typography variant="xs" style={{ marginBottom: 3 }}>
                          Token
                        </Typography>
                        <Input
                          placeholder="ie: API_TOKEN"
                          style={{ height: 38 }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="FormRow">
                      <div className="FormGroup">
                        <Typography variant="xs" style={{ marginBottom: 3 }}>
                          Search Keyword
                        </Typography>
                        <Input
                          placeholder="ie: Renew* (from subject)"
                          style={{ height: 38 }}
                        />
                      </div>

                      <div className="FormGroup" style={{ width: "100%" }}>
                        <Typography variant="xs" style={{ marginBottom: 3 }}>
                          GPT Context (AI)
                        </Typography>
                        <TextArea
                          placeholder="ie: how to parse the email content"
                          autoSize={{ minRows: 3, maxRows: 5 }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="section-divider" />

              <div className="section-header">
                <Typography
                  variant="h5"
                  weight={500}
                  style={{ marginBottom: 16, marginTop: 24 }}
                >
                  Email Followup
                </Typography>

                <div className="actions-container">
                  <Button
                    // type="primary"
                    icon={<PlusCircleFilled />}
                    onClick={addFlow}
                    className="add-flow-button"
                  >
                    Add Followup
                  </Button>
                </div>
              </div>

              {flows.map((flow) => (
                <div key={flow.id} className="flow-container">
                  <div
                    className="flow-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body" weight={400}>
                      Followup {flow.id} after{" "}
                      <Input
                        value={flow.interval}
                        onChange={(e) =>
                          handleFlowChange(flow.id, "interval", e.target.value)
                        }
                        placeholder="0"
                        style={{ width: 50, height: 32, margin: "0 8px" }}
                      />
                      days at time{" "}
                      <Input
                        value={flow.time}
                        onChange={(e) =>
                          handleFlowChange(flow.id, "time", e.target.value)
                        }
                        placeholder="00:00"
                        style={{ width: 100, height: 32, margin: "0 8px" }}
                      />
                      of{" "}
                      <Select
                        value={flow.intervalAgainst}
                        onChange={(value) =>
                          handleFlowChange(flow.id, "intervalAgainst", value)
                        }
                        placeholder="Select"
                        style={{ width: 220, height: 32, marginLeft: 8 }}
                        options={[
                          { value: "CHECKIN", label: "Check-in" },
                          { value: "CHECKOUT", label: "Check-out" },
                          { value: "BOOKING", label: "Booking Date" },
                        ]}
                      />
                    </Typography>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteFilled />}
                      onClick={() => removeFlow(flow.id)}
                      style={{ padding: 0 }}
                    />
                  </div>

                  <div className="FormRow" style={{ marginTop: 16 }}>
                    {/* <div className="FormGroup">
        <Typography variant="xs" style={{ marginBottom: 3 }}>
          Followup Name<span style={{ color: "red" }}>*</span>
        </Typography>
        <Input
          required
          value={flow.name}
          placeholder="ie: On Booking Received"
          onChange={(e) => handleFlowChange(flow.id, "name", e.target.value)}
          style={{ height: 38 }}
        />
      </div> */}

                    <div className="FormGroup">
                      <Typography variant="xs" style={{ marginBottom: 3 }}>
                        Template ID<span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Input
                        required
                        value={flow.templateId}
                        placeholder="ie: d-7256716cbab74e059a44797b6b2b7049"
                        onChange={(e) =>
                          handleFlowChange(
                            flow.id,
                            "templateId",
                            e.target.value,
                          )
                        }
                        style={{ height: 38 }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabPane>
          <TabPane tab="Column Mapping" key="columnMapping">
            <div className="ColumnMapping">
              <Typography
                variant="h5"
                weight={500}
                style={{ marginBottom: 16, marginTop: 24, display: "flex " }}
              >
                Column Mapping
                {/* Add More Button */}
                <div className="actions-container" style={{ marginTop: 0 }}>
                  <Button
                    icon={<PlusCircleFilled />}
                    onClick={() => addMappingPair()}
                    className="add-flow-button"
                  >
                    Add more
                  </Button>
                </div>
              </Typography>

              {/* Dynamic Mappings - Editable */}
              {mappings.map((mapping) => (
                <div key={mapping.id} className="FormRow">
                  <div className="FormGroup">
                    <Typography variant="xs" style={{ marginBottom: 3 }}>
                      Source Field{" "}
                      {mapping.isPredefined && (
                        <span style={{ color: "red" }}>*</span>
                      )}
                    </Typography>
                    <Input
                      //value={mapping.sourceField}
                      value={sourceFieldPlaceholders[mapping.sourceField] || ""}
                      onChange={(e) =>
                        handleMappingChange(
                          mapping.id,
                          "sourceField",
                          e.target.value,
                        )
                      }
                      disabled={mapping.isPredefined}
                      placeholder={
                        sourceFieldPlaceholders[mapping.sourceField] ||
                        "Enter source field"
                      }
                      style={{ height: 38 }}
                    />
                  </div>

                  <div
                    className="arrow-icon"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0 8px",
                    }}
                  >
                    <ArrowRightOutlined style={{ fontSize: 18 }} />
                  </div>

                  <div className="FormGroup">
                    <Typography variant="xs" style={{ marginBottom: 3 }}>
                      Target Field
                    </Typography>
                    <Input
                      value={mapping.targetField}
                      onChange={(e) =>
                        handleMappingChange(
                          mapping.id,
                          "targetField",
                          e.target.value,
                        )
                      }
                      style={{ height: 38 }}
                    />
                  </div>

                  {!mapping.isPredefined && (
                    <Button
                      danger
                      type="text"
                      icon={<DeleteFilled />}
                      onClick={() => removeMapping(mapping.id)}
                      disabled={mapping.isPredefined}
                      style={{ marginTop: "25px" }}
                    />
                  )}
                </div>
              ))}

              {/* Add More Button
              <div className="actions-container" style={{ marginTop: 16 }}>
                <Button 
                  icon={<PlusCircleFilled />} 
                  onClick={() =>
                    addMappingPair()
                  }
                  className="add-flow-button"
                >
                  Add more
                </Button>
              </div> */}
            </div>
          </TabPane>
        </Tabs>

        <div className="form-footer" style={{ marginTop: 24 }}>
          <Button type="primary" onClick={handleSave} className="save-button">
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HotelEmailConfigSetup;
